/*
 * PortfolioAnalyticsPanel
 * ───────────────────────
 * Backtest TRACK RECORD for a deployed StrategyPortfolio. Renders inline inside
 * the Strategies → Portfolio Deployments expanded panel.
 *
 * Data source: GET /portfolios/{id}/analytics/ (reads S3 parquet regenerated by
 * the regenerate_portfolio_curves Celery task). This is NOT live realized P&L —
 * it is every member strategy re-backtested from history (2016 → today) so we
 * can show per-member performance, a weighted combined equity curve (vs SPX),
 * and the cross-strategy correlation matrix even for members that never fired a
 * live order (small-book / can't-buy-one-share members still get a track record).
 *
 * The post-split slice (after each member's optimization IS/OOS split) is the
 * out-of-sample view; the combined curve shades it.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import PlotlyChart from '../../components/charts/PlotlyChart';
import { tradingService } from '../../services/tradingService';

// ── formatting helpers ───────────────────────────────────────────────────────
const num = (v) => (v == null || Number.isNaN(Number(v)) ? null : Number(v));
const fmtPct = (v) => { const n = num(v); return n == null ? '—' : `${n.toFixed(2)}%`; };
const fmtNum = (v, d = 2) => { const n = num(v); return n == null ? '—' : n.toFixed(d); };
const fmtInt = (v) => { const n = num(v); return n == null ? '—' : Math.round(n).toLocaleString(); };
const signed = (v) => {
  const n = num(v);
  if (n == null) return 'text-gray-400';
  return n > 0 ? 'text-green-600 dark:text-green-400'
       : n < 0 ? 'text-red-600 dark:text-red-400'
       : 'text-gray-500';
};

const PortfolioAnalyticsPanel = ({ portfolioId }) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(false);   // a regenerate request is in flight
  const [err, setErr]         = useState('');
  const pollRef = useRef(null);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const fetchOnce = useCallback(async () => {
    try {
      const res = await tradingService.getPortfolioAnalytics(portfolioId);
      setData(res.data);
      const st = res.data?.status;
      if (st !== 'pending' && st !== 'running') stopPoll();
      return st;
    } catch (e) {
      setErr('Could not load analytics.');
      stopPoll();
      return 'error';
    } finally {
      setLoading(false);
    }
  }, [portfolioId]);

  const startPoll = useCallback(() => {
    stopPoll();
    pollRef.current = setInterval(fetchOnce, 4000);
  }, [fetchOnce]);

  useEffect(() => {
    setLoading(true);
    fetchOnce().then((st) => { if (st === 'pending' || st === 'running') startPoll(); });
    return stopPoll;
  }, [fetchOnce, startPoll]);

  const handleRegenerate = async () => {
    setBusy(true);
    setErr('');
    try {
      await tradingService.regeneratePortfolioAnalytics(portfolioId);
      await fetchOnce();
      startPoll();
    } catch (e) {
      setErr('Could not start regeneration.');
    } finally {
      setBusy(false);
    }
  };

  const status = data?.status;
  const isRunning = status === 'pending' || status === 'running';
  const isComplete = status === 'complete';

  // ── header strip (window / computed / regenerate button) ──
  const header = (
    <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
      <h4 className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        Backtest Track Record
        {data?.window && (
          <span className="ml-2 normal-case font-normal text-gray-400">
            {data.window.start} → {data.window.end}
          </span>
        )}
      </h4>
      <div className="flex items-center gap-2">
        {isComplete && data?.computed_at && (
          <span className="text-[9px] text-gray-400">
            computed {new Date(data.computed_at).toLocaleString()}
          </span>
        )}
        <button
          onClick={handleRegenerate}
          disabled={busy || isRunning}
          className="px-2.5 py-1 text-[10px] rounded-lg font-medium bg-brand-gold/10 text-brand-gold border border-brand-gold/30 hover:bg-brand-gold/20 disabled:opacity-50 transition-colors"
        >
          {isRunning ? 'Regenerating…' : isComplete ? 'Refresh' : 'Generate'}
        </button>
      </div>
    </div>
  );

  if (loading) {
    return <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500">Loading analytics…</div>;
  }

  return (
    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
      {header}

      {err && <p className="text-[11px] text-red-500 mb-2">{err}</p>}

      {/* Empty / never-run */}
      {(status === 'none' || status === 'missing') && !isRunning && (
        <p className="text-[11px] text-gray-500 py-2">
          No track record yet. Click <span className="text-brand-gold">Generate</span> to re-run every
          member strategy from history and build per-member performance, a combined equity curve, and
          the correlation matrix. Runs in the background (~a few minutes for a large basket).
        </p>
      )}

      {/* Running */}
      {isRunning && (
        <div className="py-3">
          <div className="flex items-center justify-between text-[11px] text-gray-500 mb-1.5">
            <span>Re-running member backtests…</span>
            <span className="tabular-nums">{data?.progress || ''}</span>
          </div>
          <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-gold transition-all duration-500"
              style={{ width: data?.total ? `${Math.round((data.completed || 0) / data.total * 100)}%` : '15%' }}
            />
          </div>
        </div>
      )}

      {/* Failed */}
      {status === 'failed' && (
        <p className="text-[11px] text-red-500 py-2">
          Regeneration failed{data?.error ? `: ${data.error}` : ''}. Try again.
        </p>
      )}

      {/* Complete */}
      {isComplete && <AnalyticsBody data={data} />}
    </div>
  );
};

// ── completed-state body: table + combined curve + correlation ───────────────
const AnalyticsBody = ({ data }) => {
  const members = Array.isArray(data.members) ? data.members : [];
  // Sort by weight desc (allocation priority), valid rows first.
  const rows = [...members].sort((a, b) => {
    const ea = a.error ? 1 : 0, eb = b.error ? 1 : 0;
    if (ea !== eb) return ea - eb;
    return (num(b.weight) || 0) - (num(a.weight) || 0);
  });
  const validCount = members.filter(m => !m.error).length;
  const failed = members.filter(m => m.error);

  // ── combined equity curve traces ──
  const cc = Array.isArray(data.combined_curve) ? data.combined_curve : [];
  const spx = Array.isArray(data.spx_curve) ? data.spx_curve : [];
  const curveTraces = [];
  if (cc.length) {
    curveTraces.push({
      x: cc.map(p => p.date), y: cc.map(p => p.equity),
      type: 'scatter', mode: 'lines', name: 'Combined',
      line: { color: '#c9a96e', width: 2.5 },
    });
  }
  if (spx.length) {
    curveTraces.push({
      x: spx.map(p => p.date), y: spx.map(p => p.equity),
      type: 'scatter', mode: 'lines', name: 'SPX',
      line: { color: '#ef4444', width: 1.5, dash: 'dash' },
    });
  }
  const oos = data.oos_split;
  const lastDate = cc.length ? cc[cc.length - 1].date : null;
  const curveShapes = (oos && lastDate) ? [{
    type: 'rect', xref: 'x', yref: 'paper',
    x0: oos, x1: lastDate, y0: 0, y1: 1,
    fillcolor: '#3b82f6', opacity: 0.07, line: { width: 0 }, layer: 'below',
  }] : [];
  const curveAnnos = (oos && lastDate) ? [{
    x: oos, yref: 'paper', y: 1, xanchor: 'left', yanchor: 'top',
    text: 'OOS →', showarrow: false, font: { size: 9, color: '#3b82f6' },
  }] : [];

  // ── correlation heatmap ──
  const corr = data.correlation;

  return (
    <div className="space-y-5">
      {/* Per-member performance table */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <h5 className="text-[10px] uppercase tracking-wider text-gray-400">
            Members <span className="text-gray-500">({validCount} with track record{failed.length ? `, ${failed.length} insufficient data` : ''})</span>
          </h5>
        </div>
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-left text-gray-400 border-b border-gray-100 dark:border-gray-800">
                <th className="py-1.5 pr-3 font-medium">Strategy</th>
                <th className="py-1.5 pr-3 font-medium">Symbol</th>
                <th className="py-1.5 pr-3 font-medium">Account</th>
                <th className="py-1.5 pr-3 font-medium text-right">Weight</th>
                <th className="py-1.5 pr-3 font-medium text-right">Total Ret</th>
                <th className="py-1.5 pr-3 font-medium text-right">Ann Ret</th>
                <th className="py-1.5 pr-3 font-medium text-right">Sharpe</th>
                <th className="py-1.5 pr-3 font-medium text-right">Max DD</th>
                <th className="py-1.5 pr-3 font-medium text-right">Win %</th>
                <th className="py-1.5 pr-3 font-medium text-right">Trades</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(m => (
                <tr key={m.strategy_id} className={`border-b border-gray-50 dark:border-gray-800/50 ${m.error ? 'opacity-50' : ''}`}>
                  <td className="py-1.5 pr-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">{m.strategy_name || m.strategy_class}</td>
                  <td className="py-1.5 pr-3 font-mono text-gray-600 dark:text-gray-400">{m.symbol || '—'}</td>
                  <td className="py-1.5 pr-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    {m.account_name ? `${m.account_name} ${m.is_paper ? '(Paper)' : '(LIVE)'}` : '—'}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-gray-700 dark:text-gray-300">
                    {m.weight != null ? `${(num(m.weight) * 100).toFixed(1)}%` : '—'}
                  </td>
                  {m.error ? (
                    <td colSpan={6} className="py-1.5 pr-3 text-[10px] text-amber-500 italic">
                      {m.error === 'insufficient_curve' || (m.error || '').toLowerCase().includes('insufficient')
                        ? 'insufficient data' : m.error}
                    </td>
                  ) : (
                    <>
                      <td className={`py-1.5 pr-3 text-right tabular-nums ${signed(m.total_return)}`}>{fmtPct(m.total_return)}</td>
                      <td className={`py-1.5 pr-3 text-right tabular-nums ${signed(m.annual_return)}`}>{fmtPct(m.annual_return)}</td>
                      <td className={`py-1.5 pr-3 text-right tabular-nums ${signed(m.sharpe_ratio)}`}>{fmtNum(m.sharpe_ratio)}</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums text-red-500 dark:text-red-400">{fmtPct(m.max_drawdown)}</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums text-gray-600 dark:text-gray-400">{fmtPct(m.win_rate)}</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums text-gray-600 dark:text-gray-400">{fmtInt(m.num_trades)}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Combined equity curve */}
      {curveTraces.length > 0 && (
        <div>
          <h5 className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">
            Combined Equity Curve <span className="text-gray-500 normal-case">({data.optimizer_method || 'equal'} weighted, vs SPX)</span>
          </h5>
          <PlotlyChart
            data={curveTraces}
            layout={{
              margin: { t: 10, b: 30, l: 55, r: 10 },
              xaxis: { showgrid: false },
              yaxis: { title: { text: 'Equity ($)', font: { size: 9 } } },
              legend: { orientation: 'h', y: -0.18, font: { size: 9 } },
              shapes: curveShapes,
              annotations: curveAnnos,
              paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
            }}
            style={{ height: 240 }}
          />
        </div>
      )}

      {/* Correlation matrix */}
      {corr && corr.matrix && corr.labels && corr.labels.length >= 2 && (
        <div>
          <h5 className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Correlation Matrix</h5>
          <PlotlyChart
            interactive
            data={[{
              type: 'heatmap',
              z: corr.matrix,
              x: corr.labels.map(l => (l && l.length > 14 ? l.slice(0, 12) + '…' : l)),
              y: corr.labels.map(l => (l && l.length > 14 ? l.slice(0, 12) + '…' : l)),
              text: corr.matrix.map(r => r.map(v => v == null ? '' : v.toFixed(2))),
              texttemplate: corr.labels.length <= 20 ? '%{text}' : undefined,
              colorscale: [[0, '#ef4444'], [0.5, '#1f2937'], [1, '#22c55e']],
              zmid: 0, zmin: -1, zmax: 1, showscale: true,
              colorbar: { thickness: 10, len: 0.8, title: { text: 'ρ', side: 'right' } },
            }]}
            layout={{
              margin: { t: 10, b: 70, l: 80, r: 30 },
              xaxis: { tickangle: -30, tickfont: { size: 8 } },
              yaxis: { tickfont: { size: 8 } },
              paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
            }}
            style={{ height: Math.max(220, Math.min(corr.labels.length * 22 + 90, 700)) }}
          />
        </div>
      )}
    </div>
  );
};

export default PortfolioAnalyticsPanel;
